import { useContext, useEffect, useRef, useState } from "react"

import { AlertsContext } from "../../../../utils/providers/alerts"
import { funEU } from "../../../../utils/firebase"
import Window from "../../../structure/window/window"
import Timeline from "../../../ui/timeline/timeline"
import { buildSupplierTimeline } from "./process-timeline"

/**
 * Live "Process" modal. Opening it kicks off the supplier-document pipeline
 * (parseSupplierDocument — which also triggers the server-side specialists) and
 * renders the shared Timeline from the live Firestore snapshot the parent
 * streams in via `doc`. The "Sent to classifier" step ticks off the server's
 * own CLASSIFYING marker (surfaced via the live snapshot), so it reflects the
 * server actually starting — not just the browser firing the request. The
 * visible flow stops at the classify stage; specialist extraction runs
 * server-side and is not visualised here yet.
 *
 * Props:
 *   doc     — live supplier_contract_documents data (re-flows on each snapshot)
 *   onClose — close handler
 */
export default function ProcessModal({ doc, onClose }) {
	const { pushAlert } = useContext(AlertsContext)

	const [phase, setPhase] = useState("classifying")
	const [classification, setClassification] = useState(null)
	const [error, setError] = useState(null)

	const ranRef = useRef(false)
	const documentId = doc?.id

	// Kick off the run exactly once when the modal mounts.
	useEffect(() => {
		if (ranRef.current || !documentId) return
		ranRef.current = true

		let cancelled = false
		const run = async () => {
			try {
				const callable = funEU.httpsCallable("parseSupplierDocument", { timeout: 300000 })
				// Prompts/model/schema live in AI Tooling (ai_tooling/document-classifier);
				// the function resolves them server-side, so we only send the document id.
				const response = await callable({ document_id: documentId })
				if (cancelled) return
				setClassification(response?.data?.parsed?.classification ?? null)
				setPhase("done")
			} catch (err) {
				if (cancelled) return
				const message = err?.message || String(err)
				setError(message)
				setPhase("error")
				pushAlert({ type: "ERROR", title: "Processing failed", body: message })
			}
		}

		run()
		return () => {
			cancelled = true
		}
	}, [documentId, pushAlert])

	const data = buildSupplierTimeline(doc, { phase, classification, error })

	return (
		<Window
			title="Classify & Process Document"
			className="supplier-process-modal"
			close={() => onClose()}>
			<div className="supplier-process-modal-body">
				<Timeline data={data} />
			</div>
		</Window>
	)
}
